/**
 * HTTP Connection Pool and Timeout Management
 *
 * FIXES PERFORMANCE ISSUES:
 * • Connection Pooling: Reuses connections instead of creating new ones
 * • Request Timeouts: Fails fast (10s) instead of hanging indefinitely
 * • Resource Management: Prevents connection exhaustion and DNS lookup delays
 *
 * CONFIGURATION:
 * • maxSockets: 200 - Max concurrent connections per host
 * • maxFreeSockets: 50 - Keep 50 idle connections ready for reuse
 * • timeout: 10s - Close inactive sockets after 10 seconds
 * • freeSocketTimeout: 5s - Close unused connections after 5 seconds
 */
import type { HttpsOptions } from 'agentkeepalive';
import { HttpsAgent } from 'agentkeepalive';
import type { RequestInfo, RequestInit } from 'node-fetch';
import nodeFetch from 'node-fetch';
import { logger } from '.';

export const DEFAULT_HTTPS_OPTIONS = {
  keepAlive: true,
  maxSockets: 200,
  maxFreeSockets: 50,
  timeout: 10000,
  freeSocketTimeout: 5000,
} as const;

export const DEFAULT_REQUEST_TIMEOUT = 10000 as const;

/**
 * Creates HTTPS agent with connection pooling
 */
export const createHttpsAgent = (opt?: Omit<HttpsOptions, 'keepAlive'>): HttpsAgent =>
  new HttpsAgent({
    ...DEFAULT_HTTPS_OPTIONS,
    ...opt,
  });

export interface ExtendedRequestInit extends RequestInit {
  timeout?: number;
}

/**
 * Creates fetch function with connection pooling and timeouts
 */
export const createFetch = (opt?: Omit<HttpsOptions, 'keepAlive'>) => {
  const httpsAgent = createHttpsAgent(opt);

  return async (url: RequestInfo, options?: ExtendedRequestInit) => {
    const { timeout = DEFAULT_REQUEST_TIMEOUT, ...fetchOptions } = options || {};

    // Setup request timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await nodeFetch(url, {
        ...fetchOptions,
        agent: httpsAgent, // Use connection pool
        signal: controller.signal, // Enable timeout
      });
      return response;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        const errorMsg = `Request timeout after ${timeout}ms: ${url}`;
        logger.error({ error }, errorMsg);

        throw new Error(errorMsg);
      }
      const errorMsg = `Fetch error for ${url}: ${error.message}`;
      logger.error({ error }, errorMsg);

      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  };
};
